import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { DeleteShortlistedProfilesRequest } from './DTO/DeleteShortlistedProfilesRequest';
import { ShortlistedProfile, ShortlistedStatus } from './DTO/ShortlistedProfile';
import { UpdateShortlistedProfilesStatusRequest } from './DTO/UpdateShortlistedProfilesStatusRequest';
import Page from '@/Models/Page';
import http from '@/Utils/HTTP/NewHttpUtils';

const buildQuery = (offset: number, limit: number): string => {
    const query = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
    });
    return query.toString();
};

export const shortlistedStatusesOrder: Array<ShortlistedStatus> = [
    'NotContacted',
    'ContactedEmail',
    'ContactedInMail',
    'ContactedPhone',
    'ErrorContactManually',
    'ContactTaskInMail',
    'ContactTaskPhone',
    'RepliedEmail',
    'RepliedInMail',
    'RepliedPhone',
    'NoReply',
    'NotInterested',
    'Interested',
    'Applied',
    'ContactCancelled',
];

export const useShortlistedProfiles = (limit: number, offset: number) => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWR(
        `recruitments/${assessmentId}/ai-search/shortlisted/?${buildQuery(offset, limit)}`,
        async (path: string) => {
            const response = await http.get<Page<ShortlistedProfile>>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
    );
};

export const useDeleteShortlistedProfiles = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/shortlisted-profile/delete`,
        async (path: string, { arg }: { arg: DeleteShortlistedProfilesRequest }) => {
            await http.delete({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};

export const useUpdateShortlistedProfilesStatus = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/shortlisted-profile/status`,
        async (path: string, { arg }: { arg: UpdateShortlistedProfilesStatusRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};

export const useLinkedInProfileSearch = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/shortlisted-profile/search-by-linkedin-url`,
        async (path, { arg }: { arg: { linkedInUrl: string } }) => {
            const response = await http.post<ShortlistedProfile>({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });

            return response;
        },
        { onError: (error) => error },
    );
};

export const useShortlistLinkedInProfile = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { assessmentId } = useParams();

    return useSWRMutation(
        `recruitments/${assessmentId}/ai-search/shortlisted-profile/shortlist-linkedin-search`,
        async (path, { arg }: { arg: { profileId: string } }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};
